"use client";

import type { ReactNode } from "react";
import { useFormState, type UseFormReturn } from "react-hook-form";
import type { Message } from "@/lib/core/crm/types";
import type { EmailTemplate } from "@/lib/hooks/api/emails/email-templates";
import type { ComposeFormValues, EmailConnection } from "@/lib/types/compose-modal-types";
import { ComposeBodyField } from "./ComposeBodyField";
import { ComposeMainFields } from "./ComposeMainFields";
import { ComposeValidationSummary } from "./ComposeValidationSummary";
import { MoreRecipientsSection } from "./MoreRecipientsSection";
import { PersonalizationSection } from "./PersonalizationSection";
import { SignatureOptionsSection } from "./SignatureOptionsSection";
import { ComposeTrackOpensToggle } from "./ComposeTrackOpensToggle";
import { useComposeFormFieldsState } from "./useComposeFormFieldsState";

interface ComposeFormFieldsProps {
  form: UseFormReturn<ComposeFormValues>;
  originalMessage?: Message | null;
  emailConnections?: EmailConnection[];
  onTemplateSelect?: (template: EmailTemplate) => void;
}

export function ComposeFormFields({
  form,
  originalMessage,
  emailConnections,
  onTemplateSelect,
}: ComposeFormFieldsProps): ReactNode {
  const state = useComposeFormFieldsState({ form, originalMessage });
  // `useFormState` subscribes THIS component to the error state. Reading
  // `form.formState.errors` only re-renders the `useForm` host, which the memo
  // on `ComposeFormTabs` stops before reaching here.
  const { errors: formErrors } = useFormState({ control: form.control });

  return (
    <div className="space-y-4">
      <ComposeValidationSummary formErrors={formErrors} />

      <ComposeMainFields
        clientState={state.clientState}
        emailConnections={emailConnections}
        form={form}
        formErrors={formErrors}
        hasReplyRestrictions={state.hasReplyRestrictions}
        recipientsState={state.recipientsState}
      />

      <MoreRecipientsSection
        bccCompanyPartners={state.bccCompanyPartners}
        clientState={state.clientState}
        form={form}
        hasReplyRestrictions={state.hasReplyRestrictions}
        onBccCompanyPartnersChange={state.handleBccCompanyPartnersChange}
        onOpenChange={state.setShowMoreRecipients}
        open={state.showMoreRecipients}
        recipientsState={state.recipientsState}
      />

      <ComposeBodyField
        clientState={state.clientState}
        emailConnections={emailConnections}
        form={form}
        onAttachmentsChange={state.handleAttachmentsChange}
        onTemplateSelect={onTemplateSelect}
        isReply={Boolean(originalMessage)}
      />

      <div className="grid gap-4 xl:grid-cols-2">
        <PersonalizationSection
          clientState={state.clientState}
          hasTemplateFields={state.hasTemplateFields}
          onOpenChange={state.setShowPersonalization}
          open={state.showPersonalization}
          personalizationReplyLinked={state.personalizationReplyLinked}
          triggerSubtitle={state.personalizationTriggerSubtitle}
        />

        <SignatureOptionsSection
          form={form}
          onOpenChange={state.setShowSendOptions}
          onSignatureContent={state.setVariableValue}
          open={state.showSendOptions}
          selectedSignature={state.selectedSignature}
          watchedFromAccount={state.watchedFromAccount}
        />
      </div>

      <ComposeTrackOpensToggle form={form} />
    </div>
  );
}
